import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface HeritageErrorFallbackProps {
  /** Retry the in-page render (clears boundary state, re-attempts the import). */
  onRetry: () => void;
  /** Hard reload (last resort — fetches fresh index.html + chunk manifest). */
  onHardReload: () => void;
  /** Optional error for diagnostic display. */
  error?: Error | null;
}

/**
 * On-brand error page shown when a lazy-loaded heritage chunk fails to fetch
 * (typically: stale chunk hash after a redeploy, or a transient network drop).
 *
 * Offers a soft retry first; falls back to a hard reload that bypasses the
 * cached HTML so the browser picks up the new chunk manifest.
 */
const HeritageErrorFallback = ({
  onRetry,
  onHardReload,
  error,
}: HeritageErrorFallbackProps) => (
  <Layout>
    <section
      role="alert"
      aria-live="assertive"
      className="py-20 lg:py-28 bg-gradient-to-b from-secondary/30 to-background"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center space-y-6">
          <div className="flex items-center justify-center gap-3">
            <div className="h-px w-12 bg-gradient-to-r from-transparent via-accent/60 to-transparent" />
            <span className="font-serif text-2xl tracking-widest text-primary/90">
              Y.O.
            </span>
            <div className="h-px w-12 bg-gradient-to-r from-transparent via-accent/60 to-transparent" />
          </div>

          <div className="flex justify-center">
            <div className="rounded-full bg-accent/10 p-4 border border-accent/30">
              <AlertTriangle className="h-8 w-8 text-accent" aria-hidden="true" />
            </div>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl text-foreground">
            We couldn't load this page
          </h1>

          <div className="divider-brass" />

          <p className="text-muted-foreground leading-relaxed">
            A heritage section couldn't be reached — usually because the site was
            updated while this tab was open, or your connection briefly dropped.
            Reloading almost always fixes it.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            <Button onClick={onRetry} variant="default" className="gap-2">
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Try again
            </Button>
            <Button onClick={onHardReload} variant="outline" className="gap-2">
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Reload from server
            </Button>
            <Button asChild variant="ghost" className="gap-2">
              <Link to="/">
                <Home className="h-4 w-4" aria-hidden="true" />
                Return home
              </Link>
            </Button>
          </div>

          {error?.message && (
            <details className="text-left mt-6 mx-auto max-w-lg rounded-md border border-border/60 bg-card/50 p-4">
              <summary className="cursor-pointer text-xs uppercase tracking-widest text-muted-foreground">
                Technical details
              </summary>
              <p className="mt-2 font-mono text-xs text-muted-foreground break-all">
                {error.message}
              </p>
            </details>
          )}
        </div>
      </div>
    </section>
  </Layout>
);

export default HeritageErrorFallback;